export type QueryPrimitive = string | number | boolean | Date | null | undefined;

export type QueryValue = QueryPrimitive | QueryPrimitive[];

export type BuildQueryStringOptions = {
  /**
   * When true, prepend a leading '?' to the returned query string.
   * Defaults to false.
   */
  includeQuestionMark?: boolean;
  /**
   * When true, omit empty-string values (after trimming).
   * Defaults to true.
   */
  skipEmptyStrings?: boolean;
};

const DEFAULT_OPTIONS: Required<BuildQueryStringOptions> = {
  includeQuestionMark: false,
  skipEmptyStrings: true,
};

const toStringValue = (value: Exclude<QueryPrimitive, null | undefined>): string => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
};

export function buildQueryString(
  params: Record<string, QueryValue>,
  options?: BuildQueryStringOptions,
): string {
  const { includeQuestionMark, skipEmptyStrings } = { ...DEFAULT_OPTIONS, ...(options ?? {}) };
  const search = new URLSearchParams();

  const appendValue = (key: string, raw: QueryPrimitive) => {
    if (raw === null || raw === undefined) {
      return;
    }
    const valueString = toStringValue(raw);
    if (skipEmptyStrings && valueString.trim() === '') {
      return;
    }
    search.append(key, valueString);
  };

  Object.entries(params).forEach(([key, rawValue]) => {
    if (Array.isArray(rawValue)) {
      rawValue.forEach((value) => appendValue(key, value));
    } else {
      appendValue(key, rawValue);
    }
  });

  const queryString = search.toString();
  if (!queryString) {
    return '';
  }
  return includeQuestionMark ? `?${queryString}` : queryString;
}
